import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { VideoInfo } from "./models/videoInfo";
import SizeHandler from "./models/sizeHandler";
import { GapVM } from "./models/gapVM";
import { GapViewModel } from "./models/gapViewModel";
import ProgressBar from "./components/ProgressBar";
import VideoControls from "./components/VideoControls";
import useWindowDimensions from "../Hooks/useWindowDimensions";
import { MotionEvent } from "./models/motionEvent";
import { getVideoPlaylistForNewTimestamp } from "../../API/apiDevices";
import { ModelAspectRatio, DeviceModel } from "../../Models/devices";
import LoadingSpinner from "./components/LoadingSpinner";
import DewarpViews from "../Immervision/DewarpViews";
import { FISHEYE_VIEW_TYPES } from "../../../Pages/Cameras/components/PlaybackWebRTC/FishEyeGridSettings";
import { HlsHandler } from "../Hooks/HlsHandler";
import { FishEyePlayerModel } from "./models/FishEyePlayerModel";

type DewarpViewType = {
  changeView: (v: FISHEYE_VIEW_TYPES) => void;
};

interface Props {
  videoInfo: VideoInfo | null;
  motionEvents: MotionEvent[] | null;
  startDateIsoStr: string;
  skipToDateTimeISO?: string;
  serialNumber: string;
  onEnded?: (endDateISO: string) => void;
  isSDLowPlayback: () => boolean;
  timezone: string | undefined;
  videoTitle: string;
  modelAspectRatio?: ModelAspectRatio;
  isFullScreen: boolean;
  setIsFullScreen: Dispatch<SetStateAction<boolean>>;
  openShareDialog: () => void;
  shouldDisplayShareButton: boolean;
  onError: (errorMessage: string) => void;
  shouldDisplayDownloadButton: boolean;
  device?: DeviceModel;
  fishEyePlayerModel?: FishEyePlayerModel;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    videoContainer: {
      position: "relative",
    },
    container: { backgroundColor: "#000" },
  })
);

const Player = ({
  videoInfo,
  motionEvents,
  startDateIsoStr,
  skipToDateTimeISO,
  serialNumber,
  onEnded,
  isSDLowPlayback,
  timezone,
  videoTitle,
  modelAspectRatio,
  isFullScreen,
  setIsFullScreen,
  openShareDialog,
  shouldDisplayShareButton,
  onError,
  shouldDisplayDownloadButton,
  device,
  fishEyePlayerModel,
}: Props) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlBarRef = useRef(null);
  const sizeHandler = new SizeHandler(modelAspectRatio);
  const [displayTime, setDisplayTime] = useState(0);
  const [motionEventBarVisibility, setMotionEventBarVisibility] = useState({
    person: true,
    vehicle: true,
    animal: true,
  });
  const [hasVideoLoaded, setHasVideoLoaded] = useState(false);
  const [isVideoPlaying, setVideoPlaying] = useState(false);
  const [videoHeight, setVideoHeight] = useState(sizeHandler.videoHeight);
  const [videoWidth, setVideoWidth] = useState(sizeHandler.videoWidth);
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(60);
  const [gapsViewModel, setGapsViewModel] = useState<GapViewModel[]>([]);
  const { windowWidth, windowHeight } = useWindowDimensions();

  const onWindowSizeDidChange = () => {
    sizeHandler.resizeHandler(videoRef.current, controlBarRef.current, windowWidth, windowHeight);
    setVideoWidth(sizeHandler.videoWidth);
    setVideoHeight(sizeHandler.videoHeight);
  };

  useEffect(onWindowSizeDidChange, [hasVideoLoaded, windowHeight, windowWidth]);

  const updateGapsViewModel = () => {
    if (!videoInfo) {
      return;
    }
    const startDateUnix = new Date(videoInfo.startDateISO).getTime();
    const gapsViewModel = videoInfo.gaps.map(
      (gap) => new GapViewModel(gap, videoDurationSeconds, startDateUnix, videoWidth)
    );
    setGapsViewModel(gapsViewModel);
  };

  useEffect(updateGapsViewModel, [videoWidth, videoInfo, videoDurationSeconds]);

  const videoDurationDidChange = () => {
    if (!videoRef.current?.duration) {
      return;
    }
    if (!videoInfo) {
      setVideoDurationSeconds(videoRef.current.duration);
      return;
    }
    const gapsDurationSeconds = videoInfo.gaps
      .map((gap) => gap.durationSeconds)
      .reduce((a, b) => a + b, 0);
    setVideoDurationSeconds(videoRef.current.duration + gapsDurationSeconds);
  };

  const getGapDurationBeforeUTCTime = (dateISO: string) => {
    let totalSeconds = 0;

    if (!videoInfo || !dateISO) {
      return 0;
    }

    const skipDate = new Date(dateISO);

    for (const gap of videoInfo.gaps) {
      const gapDate = new Date(gap.startTime);

      if (gapDate > skipDate) {
        return totalSeconds;
      }
      totalSeconds += gap.durationSeconds;
    }

    return totalSeconds;
  };

  function getSecondsToSkip(): number {
    if (!skipToDateTimeISO || !startDateIsoStr) {
      return 0;
    }
    const skipToDate = new Date(skipToDateTimeISO);
    const startDate = new Date(startDateIsoStr);
    const skipToDisplayTimeIdealSeconds = (skipToDate.getTime() - startDate.getTime()) / 1000;
    const totalGapDuration = getGapDurationBeforeUTCTime(skipToDateTimeISO);
    return skipToDisplayTimeIdealSeconds - totalGapDuration;
  }

  const [is404Refetching, setIs404Refetching] = useState(false);
  const [hlsHandler, setHlsHandler] = useState<HlsHandler | null>(null);

  const onDataWasUpdated = (secondsToSkip = getSecondsToSkip()) => {
    if (!videoRef || !videoRef.current || !videoInfo) {
      return;
    }
    const hlsHandlerLocal = new HlsHandler(
      videoRef.current,
      videoInfo.url,
      secondsToSkip,
      onError,
      true,
      (isRefetching: boolean) => setIs404Refetching(isRefetching)
    );
    setHlsHandler(hlsHandlerLocal);
  };

  useEffect(() => () => hlsHandler?.destroy(), [hlsHandler]);

  useEffect(onDataWasUpdated, [videoRef, videoInfo]);

  const handleDurationChange = () => {
    if (videoRef && videoRef.current) videoRef.current.ondurationchange = videoDurationDidChange;
  };

  useEffect(handleDurationChange, [videoRef]);

  const handleTimeUpdate = () => {
    try {
      if (!(videoRef.current && videoInfo)) {
        throw new Error("Required video data is missing.");
      }
      const newElapsedTime = videoRef.current.currentTime;
      const newDisplayTime =
        newElapsedTime +
        getTotalGapDurationOfAllGapsThatOccurBeforeGivenVideoTimestamp(newElapsedTime);
      setDisplayTime(newDisplayTime);
    } catch (error) {
      console.error(error);
    }
  };

  const getTotalGapDurationOfAllGapsThatOccurBeforeGivenVideoTimestamp = (timestamp: number) => {
    try {
      if (!videoInfo) {
        throw new Error("Video data is not available.");
      }
      return videoInfo.gaps.reduce((acc, g) => {
        return getGapOccurrenceTimeOnVideoTimelineByGap(g) < timestamp
          ? acc + g.durationSeconds
          : acc;
      }, 0);
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const getGapOccurrenceTimeOnVideoTimelineByGap = (gap: GapVM) => {
    try {
      if (!videoInfo) {
        throw new Error("Video data is not available.");
      }
      const totalDurationOfGapsThatStartBeforeGivenStartTime = videoInfo.gaps.reduce((acc, g) => {
        return ISOtoSecondsElapsedInPlayer(g.startTime) < ISOtoSecondsElapsedInPlayer(gap.startTime)
          ? acc + g.durationSeconds
          : acc;
      }, 0);
      return (
        ISOtoSecondsElapsedInPlayer(gap.startTime) -
        totalDurationOfGapsThatStartBeforeGivenStartTime
      );
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const ISOtoSecondsElapsedInPlayer = (timestamp: string) => {
    const correctedTimestamp = timestamp.endsWith("Z") ? timestamp : `${timestamp}Z`;
    const ellapsedMs = new Date(correctedTimestamp).getTime() - new Date(startDateIsoStr).getTime();
    return ellapsedMs / 1000;
  };

  async function loadUpdatedPlaylists(newStartDateTime: Date) {
    const response = await getVideoPlaylistForNewTimestamp(
      serialNumber,
      newStartDateTime.toISOString()
    );
    if (response.data.hasErrors) {
      console.log(response);
    }
  }

  const timeEnded = () => {
    setVideoPlaying(false);
    if (onEnded) {
      const currentDateTimeUnix = new Date(startDateIsoStr).getTime() + displayTime * 1000;
      const currentDateTimeISO = new Date(currentDateTimeUnix).toISOString();
      onEnded(currentDateTimeISO);
    }
  };

  const videoPlaying = () => {
    setVideoPlaying(true);
  };

  const onLoadedMetadata = () => {
    setHasVideoLoaded(true);
    setVideoPlaying(true);
  };

  const videoAspectRatio = videoWidth / videoHeight;

  const dewarperRef = useRef<DewarpViewType>(null);
  const changeDewarperView = (viewType: FISHEYE_VIEW_TYPES) => {
    dewarperRef.current?.changeView(viewType);
  };
  const requestVideoToBeRerendered = () => {
    const secondsToSkip = videoRef.current?.currentTime || 0;
    onDataWasUpdated(secondsToSkip);
  };

  return (
    <div
      data-testid="anavio-player"
      className={classes.container}
      style={{ backgroundColor: "#000" }}
    >
      {is404Refetching && !isVideoPlaying && <LoadingSpinner />}
      <div className={classes.videoContainer} style={{ position: "relative" }}>
        <DewarpViews
          ref={dewarperRef}
          videoRef={videoRef}
          device={device}
          requestVideoToBeRerendered={requestVideoToBeRerendered}
          fishEyePlayerModel={fishEyePlayerModel}
        />
        <video
          ref={videoRef}
          controls
          autoPlay
          width={videoWidth}
          height={videoHeight}
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={() => handleTimeUpdate()}
          onEnded={timeEnded}
          onPlaying={videoPlaying}
        />
      </div>

      <div ref={controlBarRef}>
        <ProgressBar
          displayTime={displayTime}
          timezone={timezone}
          ISOtoSeconds={ISOtoSecondsElapsedInPlayer}
          motionEvents={motionEvents}
          realTimespanLength={videoDurationSeconds}
          startDateTime={startDateIsoStr}
          videoInfo={videoInfo}
          videoRef={videoRef}
          videoWidth={videoWidth}
          videoAspectRatio={videoAspectRatio}
          loadUpdatedPlaylists={loadUpdatedPlaylists}
          motionEventBarVisibility={motionEventBarVisibility}
          gapsViewModel={gapsViewModel}
          serialNumber={serialNumber}
        />

        <VideoControls
          serialNumber={serialNumber}
          startDateIsoStr={startDateIsoStr}
          realtimeSecondsElapsed={displayTime}
          videoInfo={videoInfo}
          videoRef={videoRef}
          videoLength={
            videoDurationSeconds -
            getTotalGapDurationOfAllGapsThatOccurBeforeGivenVideoTimestamp(videoDurationSeconds)
          }
          timezone={timezone}
          videoWidth={videoWidth}
          videoTitle={videoTitle}
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
          openShareDialog={openShareDialog}
          motionEventBarVisibility={motionEventBarVisibility}
          setMotionEventBarVisibility={setMotionEventBarVisibility}
          shouldDisplayShareButton={shouldDisplayShareButton}
          shouldDisplayDownloadButton={shouldDisplayDownloadButton}
          device={device}
          changeDewarperView={changeDewarperView}
        />
      </div>
    </div>
  );
};

export default Player;
